'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  Sparkles, Lock, RefreshCw, AlertTriangle, TrendingUp, Building2,
  MessageSquareQuote, ShieldAlert, History, ChevronDown, ChevronUp, FileText,
} from 'lucide-react';
import { getApiBase } from '@/lib/apiBase';
import {
  EARNINGS_CONFIG,
  type EarningsData,
  type EarningsAnalysisResponse,
  type EarningsAnalysisStatusResponse,
  type EarningsHistoryEntry,
} from '@/types';
import { EarningsLimitModal } from './EarningsLimitModal';
import { Modal } from './Modal';
import { Button } from './Button';

interface EarningsAnalysisProps {
  symbol: string;
  isPaid: boolean;
  embedded?: boolean;
}

export const EarningsAnalysis: React.FC<EarningsAnalysisProps> = ({ symbol, isPaid, embedded }) => {
  const [earnings, setEarnings] = useState<EarningsData | null>(null);
  const [sources, setSources] = useState<string[]>([]);
  const [analyzedAt, setAnalyzedAt] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<EarningsHistoryEntry[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [usage, setUsage] = useState<{ used: number; limit: number }>({
    used: 0,
    limit: EARNINGS_CONFIG.monthlyLimitPerUser,
  });
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [lastEarningsAt, setLastEarningsAt] = useState<string | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const historyMenuRef = useRef<HTMLDivElement>(null);

  // Collapse the report whenever a different version becomes active so
  // the user always lands on the teaser first.
  useEffect(() => {
    setExpanded(false);
  }, [analyzedAt]);

  const loadStatus = useCallback(async () => {
    if (!isPaid) {
      setStatusLoading(false);
      return;
    }
    setStatusLoading(true);
    setError(null);

    try {
      const res = await fetch(`${getApiBase()}/api/earnings/${encodeURIComponent(symbol)}`);
      if (res.status === 403) {
        setError('Pro subscription required');
        return;
      }
      if (!res.ok) {
        throw new Error(`Status check failed: ${res.status}`);
      }
      const data: EarningsAnalysisStatusResponse = await res.json();
      if (data.usage) setUsage(data.usage);
      setLastEarningsAt(data.lastEarningsAt ?? null);
      if (data.hasAnalysis && data.earnings) {
        setEarnings(data.earnings);
        setSources(data.sources || []);
        setAnalyzedAt(data.analyzedAt);
      } else {
        setEarnings(null);
        setSources([]);
        setAnalyzedAt(null);
      }
    } catch {
      setError('Unable to load earnings analysis. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  }, [symbol, isPaid]);

  const loadHistory = useCallback(async () => {
    if (!isPaid) return;
    try {
      const res = await fetch(`${getApiBase()}/api/earnings/${encodeURIComponent(symbol)}/history`);
      if (!res.ok) return;
      const body: { analyses: EarningsHistoryEntry[] } = await res.json();
      setHistory(body.analyses);
    } catch (err) {
      console.warn('Failed to load earnings history:', err);
    }
  }, [symbol, isPaid]);

  const generateAnalysis = useCallback(async () => {
    setGenerating(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    try {
      const res = await fetch(`${getApiBase()}/api/earnings/${encodeURIComponent(symbol)}`, {
        method: 'POST',
        signal: controller.signal,
      });
      if (res.status === 403) {
        setError('Pro subscription required');
        return;
      }
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        setUsage({
          used: typeof body?.used === 'number' ? body.used : usage.used,
          limit: typeof body?.limit === 'number' ? body.limit : usage.limit,
        });
        setShowLimitModal(true);
        return;
      }
      if (!res.ok) {
        throw new Error(`Earnings API ${res.status}`);
      }
      const data: EarningsAnalysisResponse = await res.json();
      setEarnings(data.earnings);
      setSources(data.sources || []);
      setAnalyzedAt(data.analyzedAt);
      if (data.usage) setUsage(data.usage);
      loadHistory();
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      const isAbort = e.name === 'AbortError';
      setError(
        isAbort
          ? 'Earnings analysis took too long to generate. Please try again.'
          : 'Unable to analyze earnings. Please try again.'
      );
    } finally {
      clearTimeout(timeoutId);
      setGenerating(false);
    }
  }, [symbol, loadHistory, usage.used, usage.limit]);

  const loadHistoricalAnalysis = useCallback(async (target: string) => {
    setHistoryOpen(false);
    if (target === analyzedAt) return;

    setStatusLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getApiBase()}/api/earnings/${encodeURIComponent(symbol)}/history/${encodeURIComponent(target)}`
      );
      if (!res.ok) {
        throw new Error(`History API ${res.status}`);
      }
      const data: EarningsAnalysisResponse = await res.json();
      setEarnings(data.earnings);
      setSources(data.sources || []);
      setAnalyzedAt(data.analyzedAt);
    } catch {
      setError('Unable to load that analysis. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  }, [symbol, analyzedAt]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!historyOpen) return;
    function handleClick(e: MouseEvent) {
      if (historyMenuRef.current && !historyMenuRef.current.contains(e.target as Node)) {
        setHistoryOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [historyOpen]);

  if (!isPaid) {
    return (
      <div className={embedded ? '' : 'rounded-2xl p-6'} style={embedded ? undefined : { backgroundColor: 'var(--color-surface)' }}>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <Sparkles size={18} style={{ color: 'var(--color-accent-muted)' }} />
          AI Earnings Analysis
        </h3>
        <div className="text-center py-4">
          <div className="mx-auto mb-3 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 20%, transparent)' }}>
            <Lock className="w-5 h-5" style={{ color: 'var(--color-accent-muted)' }} />
          </div>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <Link href="/checkout" className="font-medium" style={{ color: 'var(--color-accent-muted)' }}>Upgrade to Pro</Link>{' '}
            to unlock AI-powered earnings analysis.
          </p>
        </div>
      </div>
    );
  }

  const hasData = !!earnings;
  const showLoading = statusLoading || generating;
  const remaining = Math.max(0, usage.limit - usage.used);
  const outOfCredits = remaining <= 0;

  // The user's most recent stored analysis — the dropdown may surface an
  // older version, but the staleness comparison should always be against
  // their newest one.
  const latestAnalyzedAt = history[0]?.analyzedAt ?? analyzedAt ?? null;
  // True when the user already has an analysis AND the most recent past
  // earnings call happened on or before that analysis (or the timestamp
  // is unknown). In that case the underlying data hasn't moved, so we
  // confirm before spending a credit.
  const noNewEarningsSinceLatest =
    !!latestAnalyzedAt &&
    (!lastEarningsAt || new Date(lastEarningsAt).getTime() <= new Date(latestAnalyzedAt).getTime());

  const requestGenerate = () => {
    if (outOfCredits) {
      setShowLimitModal(true);
      return;
    }
    if (noNewEarningsSinceLatest) {
      setShowRegenerateConfirm(true);
      return;
    }
    generateAnalysis();
  };

  const confirmRegenerate = () => {
    setShowRegenerateConfirm(false);
    generateAnalysis();
  };

  return (
    <div className={embedded ? '' : 'rounded-2xl p-6'} style={embedded ? undefined : { backgroundColor: 'var(--color-surface)' }}>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <Sparkles size={18} style={{ color: 'var(--color-accent-muted)' }} />
          AI Earnings Analysis
        </h3>
        {!showLoading && (
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <div className="relative" ref={historyMenuRef}>
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-1 rounded-lg text-sm transition-colors"
                  style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
                >
                  <History size={12} />
                  Past analyses ({history.length})
                  <ChevronDown size={12} />
                </button>
                {historyOpen && (
                  <ul
                    className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl z-20 max-h-96 overflow-y-auto py-1"
                    style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border-subtle)' }}
                  >
                    {history.map((h) => {
                      const date = new Date(h.analyzedAt);
                      const isActive = h.analyzedAt === analyzedAt;
                      return (
                        <li key={h.analyzedAt}>
                          <button
                            type="button"
                            onClick={() => loadHistoricalAnalysis(h.analyzedAt)}
                            className="w-full flex items-start justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-[var(--color-surface-secondary)]"
                            style={{ color: 'var(--color-text)' }}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              <FileText size={14} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                              <div className="min-w-0">
                                <div className="font-medium">
                                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                                <div className="text-[11px] text-[var(--color-text-muted)] truncate">
                                  {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                  {h.quarterEnded ? ` · Q/E ${h.quarterEnded}` : ''}
                                </div>
                              </div>
                            </div>
                            {isActive && (
                              <span className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'var(--color-accent-muted)' }}>
                                current
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <button
              onClick={requestGenerate}
              disabled={outOfCredits}
              className="text-sm flex items-center gap-1 px-3 py-1 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'var(--color-accent-muted)', backgroundColor: 'color-mix(in srgb, var(--color-accent) 10%, transparent)' }}
            >
              <RefreshCw size={12} />
              {hasData ? 'Generate new' : 'Analyze'}
            </button>
          </div>
        )}
      </div>

      {!showLoading && (
        <div className="flex items-center gap-2 text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
          <Sparkles size={12} />
          <span>
            {remaining} of {usage.limit} analyses remaining this month
          </span>
        </div>
      )}

      {showLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-accent-subtle)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {generating ? `Analyzing earnings for ${symbol}...` : 'Loading...'}
            </p>
          </div>
        </div>
      )}

      {error && !showLoading && (
        <div className="text-center py-4">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-[var(--color-text-secondary)]">{error}</p>
          <button
            onClick={generateAnalysis}
            className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      {earnings && !showLoading && !error && (
        <>
          {/* Header — always visible */}
          <div className="mb-4">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{earnings.title}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Quarter Ended: {earnings.quarterEnded} | Reported: {earnings.reportedDate}
            </p>
          </div>

          {/* Bottom Line — always visible as the teaser */}
          <div className="rounded-lg px-3 py-2.5 mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 8%, transparent)' }}>
            <p className="text-xs">
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Bottom Line: </span>
              <span style={{ color: 'var(--color-text-secondary)' }}>{earnings.bottomLine}</span>
            </p>
          </div>

          {expanded && (
            <>
              {/* Key Financial Metrics Table */}
              <div className="rounded-lg overflow-hidden mb-4 border" style={{ borderColor: 'var(--color-border)' }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 8%, transparent)' }}>
                      <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Metric</th>
                      <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Value</th>
                      <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>YoY Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.metrics.map((m, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                        <td className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{m.metric}</td>
                        <td className="px-3 py-2 text-xs text-right font-medium" style={{ color: 'var(--color-text-primary)' }}>{m.value}</td>
                        <td className="px-3 py-2 text-xs text-right font-medium" style={{
                          color: m.yoyChange.startsWith('+') ? 'var(--color-positive, #22c55e)' :
                                 m.yoyChange.startsWith('-') ? 'var(--color-negative, #ef4444)' :
                                 'var(--color-text-muted)'
                        }}>{m.yoyChange}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sections Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <SectionCard
                  icon={<Building2 size={14} />}
                  title="Segment Highlights"
                  items={earnings.segmentHighlights}
                />
                <SectionCard
                  icon={<TrendingUp size={14} />}
                  title="Gross Margins"
                  items={earnings.grossMargins}
                />
                <SectionCard
                  icon={<MessageSquareQuote size={14} />}
                  title="Management Commentary"
                  items={earnings.managementCommentary}
                />
                <SectionCard
                  icon={<ShieldAlert size={14} />}
                  title="Key Risks"
                  items={earnings.keyRisks}
                />
              </div>

              <EarningsFooter sources={sources} analyzedAt={analyzedAt} />
            </>
          )}

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{
              color: 'var(--color-accent-muted)',
              backgroundColor: 'color-mix(in srgb, var(--color-accent) 6%, transparent)',
            }}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                Show less <ChevronUp size={14} />
              </>
            ) : (
              <>
                Show full report <ChevronDown size={14} />
              </>
            )}
          </button>
        </>
      )}

      {!hasData && !showLoading && !error && (
        <p className="text-sm text-center py-4" style={{ color: 'var(--color-text-muted)' }}>
          Click &quot;Analyze&quot; to get AI-powered earnings analysis for {symbol}.
        </p>
      )}

      <EarningsLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        used={usage.used}
        limit={usage.limit}
      />

      <RegenerateConfirmModal
        isOpen={showRegenerateConfirm}
        onClose={() => setShowRegenerateConfirm(false)}
        onConfirm={confirmRegenerate}
        symbol={symbol}
        latestAnalyzedAt={latestAnalyzedAt}
        remaining={remaining}
      />
    </div>
  );
};

interface RegenerateConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  symbol: string;
  latestAnalyzedAt: string | null;
  remaining: number;
}

function RegenerateConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  symbol,
  latestAnalyzedAt,
  remaining,
}: RegenerateConfirmModalProps) {
  const formattedDate = latestAnalyzedAt
    ? new Date(latestAnalyzedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'recently';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Regenerate analysis for ${symbol.toUpperCase()}?`}>
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Your most recent analysis was generated on{' '}
          <span className="font-medium text-[var(--color-text-primary)]">{formattedDate}</span>,
          and no new earnings report or call has been published since then.
          Generating a new one is unlikely to change the underlying analysis.
        </p>
        <p className="text-sm font-medium text-[var(--color-text-primary)]">
          Do you still want to generate the report?
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          This will use 1 of your {remaining} remaining analyses this month.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            <Sparkles size={14} className="mr-2" />
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function SectionCard({ icon, title, items }: { icon: React.ReactNode; title: string; items: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg p-3 border" style={{ borderColor: 'var(--color-border)', backgroundColor: 'color-mix(in srgb, var(--color-surface) 50%, transparent)' }}>
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ color: 'var(--color-accent-muted)' }}>{icon}</span>
        <p className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</p>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
            <span style={{ color: 'var(--color-text-muted)' }}>•</span> {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EarningsFooter({ sources, analyzedAt }: { sources: string[]; analyzedAt: string | null }) {
  return (
    <>
      {sources.length > 0 && (
        <div className="pt-3" style={{ borderTop: '1px solid var(--color-divide)' }}>
          <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>Sources</p>
          <div className="flex flex-wrap gap-1.5">
            {sources.map((url, i) => {
              let hostname = '';
              try { hostname = new URL(url).hostname.replace('www.', ''); } catch { hostname = url; }
              return (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                  className="text-xs px-2 py-0.5 rounded-full border hover:opacity-80 transition-opacity"
                  style={{ color: 'var(--color-accent-muted)', borderColor: 'var(--color-border)' }}>
                  {hostname}
                </a>
              );
            })}
          </div>
        </div>
      )}
      {analyzedAt && (
        <p className="text-xs mt-3" style={{ color: 'var(--color-text-muted)' }}>
          Generated {new Date(analyzedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
      <div className="mt-3 rounded-lg p-3 border" style={{ backgroundColor: 'rgba(245,158,11,0.05)', borderColor: 'rgba(245,158,11,0.2)' }}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-400/70">
            AI-generated analysis based on publicly available information. This is not financial advice. Always do your own research before making investment decisions.
          </p>
        </div>
      </div>
    </>
  );
}
